import { StemType, TICKS_PER_LOOP } from '@arpeggi/shared'
import { useEffect, useRef, useState } from 'react'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import useKeyPressed from '../../../hooks/useKeyPressed'
import { useTreeState } from '../../../stores/Root'
import { IClipStore } from '../../../stores/song/clip/ClipStore'
import { IAudioStemStore } from '../../../stores/song/stem/AudioStemStore'
import { undoManager } from '../../../stores/StudioStore'
import { clampToValue } from '../../core/Utils'
import { RefsType } from '../Context'

export const getClipClampFraction = (instrumentLoopWidthPx: number): number => {
  return instrumentLoopWidthPx > 400 ? 64 : 32
}

/**
 * MUST BE USED WITHIN THE TRACKTABLE as this references context used within it
 * @param clip the clip this hook references
 * @param instrumentLoopWidthPX the variable that's available in the studio representing instrument
 * loop widths in the composition view
 * @param stemIndex the index of the stem the clip is in
 */
const useClip = (
  clip: IClipStore,
  instrumentLoopWidthPX: number,
  stemIndex: number,
  instrumentTrackRefs: RefsType
) => {
  const [audioDataArr, setAudioDataArr] = useState<Float32Array>(new Float32Array())
  const [title, setTitle] = useState<string>(null)
  const { studio } = useTreeState()
  const shiftPressed = useKeyPressed(16)
  const stem: IAudioStemStore = studio.song.stems[stemIndex] as IAudioStemStore
  const clipContainer = useRef<HTMLDivElement>(null)
  const tempo = studio.song.tempo
  const songLengthIn48n = studio.song.songLengthIn48n
  const left = instrumentLoopWidthPX * (clip.startInComposition48n / TICKS_PER_LOOP)
  const width =
    instrumentLoopWidthPX *
    ((clip.endInComposition48n - clip.startInComposition48n) / TICKS_PER_LOOP)
  const clampFraction = getClipClampFraction(instrumentLoopWidthPX)

  const clipAudioSourceLengthPx =
    (clip.getAudioSourceDuration48n(studio.song.tempo) * instrumentLoopWidthPX) / TICKS_PER_LOOP
  const startOffsetPx = (clip.startOffsetPercent(studio.song.tempo) * clipAudioSourceLengthPx) / 100
  const endOffsetPx = (clip.endOffsetPercent(studio.song.tempo) * clipAudioSourceLengthPx) / 100

  const deltaPxToDelta48n = (deltaPx: number, relativeSide: 'left' | 'right'): number => {
    const relativeValue =
      relativeSide === 'left' ? clip.startInComposition48n : clip.endInComposition48n
    const preprocessedDelta48n = Math.round((deltaPx / instrumentLoopWidthPX) * TICKS_PER_LOOP)
    const clampedStart = clip.snapToGrid
      ? clampToValue(preprocessedDelta48n + relativeValue, TICKS_PER_LOOP / clampFraction)
      : preprocessedDelta48n + relativeValue
    const delta48n = clampedStart - relativeValue
    return delta48n
  }

  const onMove = (deltaPX: number): boolean => {
    if (clip.initializing || !ArpRegistry.readyForStudioUse) return false
    const delta48n = deltaPxToDelta48n(deltaPX, 'left')

    if (delta48n === 0) return false

    if (studio.selectedClipIDs.includes(clip.uniqueId)) {
      const successes = studio.selectedClipIDs.map((selectedClip) =>
        studio.song.getClipByID(selectedClip).move48n(delta48n, songLengthIn48n)
      )
      return successes.find((item) => item)
    } else {
      return clip.move48n(delta48n, songLengthIn48n)
    }
  }

  const onResizeLeft = (deltaPX: number): boolean => {
    if (clip.initializing || !ArpRegistry.readyForStudioUse) return false
    const delta48n = deltaPxToDelta48n(deltaPX, 'left')

    if (studio.selectedClipIDs.includes(clip.uniqueId)) {
      const successes = studio.selectedClipIDs.map((selectedClip) =>
        studio.song.getClipByID(selectedClip).moveLeftSide48n(delta48n, tempo)
      )
      return successes.find((item) => item)
    } else {
      return clip.moveLeftSide48n(delta48n, tempo)
    }
  }

  const onResizeRight = (deltaPX: number): boolean => {
    if (clip.initializing || !ArpRegistry.readyForStudioUse) return false
    const delta48n = deltaPxToDelta48n(deltaPX, 'right')

    if (studio.selectedClipIDs.includes(clip.uniqueId)) {
      const successes = studio.selectedClipIDs.map((selectedClip) =>
        studio.song.getClipByID(selectedClip).moveRightSide48n(delta48n, tempo, songLengthIn48n)
      )
      return successes.find((item) => item)
    } else {
      return clip.moveRightSide48n(delta48n, tempo, songLengthIn48n)
    }
  }

  const onStretch = (deltaPX: number): boolean => {
    if (clip.initializing || !ArpRegistry.readyForStudioUse) return false
    const delta48n = deltaPxToDelta48n(deltaPX, 'right')

    if (studio.selectedClipIDs.includes(clip.uniqueId)) {
      const successes = studio.selectedClipIDs.map((selectedClip) =>
        studio.song.getClipByID(selectedClip).warpRightSide48n(delta48n, tempo, songLengthIn48n)
      )
      return successes.find((item) => item)
    } else {
      return clip.warpRightSide48n(delta48n, tempo, songLengthIn48n)
    }
  }

  const onClick = () => {
    if (clip.initializing || !ArpRegistry.readyForStudioUse) return false
    if (!shiftPressed) studio.setSelectedStemIndex(stemIndex, false)
    studio.setSelectedClipID(clip.uniqueId, shiftPressed)
  }

  useEffect(() => {
    const handle = clipContainer.current
    if (!handle || !ArpRegistry.readyForStudioUse) return
    handle.onmousedown = (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      let mouseMovedDistance = 0
      let pressed = false
      let pressedX = -1
      let boundingRect = { top: -1, bottom: -1 }

      boundingRect = instrumentTrackRefs[stem.uniqueId]?.getBoundingClientRect() || boundingRect

      if (e.button !== 0) return

      studio.disableAudioUpdates()

      undoManager.startGroup(() => {
        pressedX = e.pageX
        pressed = true

        const onMouseMove = (evt: MouseEvent) => {
          evt.preventDefault()
          evt.stopPropagation()

          const clickX = evt.pageX
          mouseMovedDistance = Math.max(mouseMovedDistance, clickX - pressedX)

          // Dealing with x-axis movement
          const updateSuccessful = onMove(clickX - pressedX)
          if (updateSuccessful) {
            pressedX = clip.snapToGrid
              ? clampToValue(clickX, instrumentLoopWidthPX / clampFraction)
              : clickX
          }

          // can't vertically move remixed clips
          if (stem.isRemixed) return

          // Dealing with y-axis movement
          const clickY = evt.pageY
          // If the dragged mouse Y coordinate is inside of another audio stem,
          // move the clip to that stem
          studio.song.stems.forEach((otherStem, otherStemIndex: number) => {
            if (
              otherStem.type() !== StemType.AUDIO ||
              otherStemIndex === studio.song.getClipParentStemIndex(clip.uniqueId) ||
              otherStem.isRemixed // can't move clips into a remixed stem
            ) {
              return
            }
            const trackCoords = document
              .getElementById(`audio-stem-${otherStem.uniqueId}`)
              ?.getBoundingClientRect()
            if (!trackCoords) return
            if (clickY < trackCoords.bottom && clickY > trackCoords.top) {
              if (studio.selectedClipIDs.includes(clip.uniqueId)) {
                const stemIndexIncreasing = otherStemIndex > stemIndex
                const selectedClipParentStemIndexes = studio.selectedClipIDs.map((selectedClipID) =>
                  studio.song.getClipParentStemIndex(selectedClipID)
                )
                const neighborIndexes = selectedClipParentStemIndexes.map((parentStemIndex) =>
                  studio.song.getNeighboringAudioStemIndex(
                    parentStemIndex,
                    stemIndexIncreasing ? 'below' : 'above'
                  )
                )
                if (neighborIndexes.includes(null)) return
                studio.selectedClipIDs.forEach((clipID, i) => {
                  studio.song.moveClipToStem(clipID, neighborIndexes[i])
                })
                studio.setSelectedStemIndex(
                  studio.song.getClipParentStemIndex(clip.uniqueId),
                  false
                )
              } else {
                studio.song.moveClipToStem(clip.uniqueId, otherStemIndex)
                studio.setSelectedStemIndex(studio.song.getClipParentStemIndex(clip.uniqueId))
                studio.setSelectedClipID(clip.uniqueId)
              }
              boundingRect = trackCoords
              return
              //TODO: changing the order of the stems fucks things up and doesn't let you move a clip into it
            }
          })
        }
        const onMouseUp = (evt: MouseEvent) => {
          e.preventDefault()
          undoManager.stopGroup()

          if (mouseMovedDistance < 3) onClick()

          mouseMovedDistance = 0
          pressed = false
          document.removeEventListener('mousemove', onMouseMove)
          studio.fetchSamplesForCurrentSongAsync().then(() => {
            studio.enableAudioUpdates()
          })
        }
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp, { once: true })
      })
    }

    return () => {}
  }, [
    instrumentLoopWidthPX,
    clipContainer.current,
    stemIndex,
    studio.selectedClipIDs,
    instrumentTrackRefs,
    onStretch,
    clip?.snapToGrid,
    tempo
  ])

  useEffect(() => {
    clip.sampleWithEffects.getSampleBuffer().then((decodedAudio) => {
      setAudioDataArr(decodedAudio.getChannelData(0))

      const metadata = clip.sampleWithEffects.getMetadata()
      setTitle(`${metadata.metadata.title} - ${metadata.user.displayName}`)
    })
  }, [
    clip.uniqueId,
    clip.initializing,
    clip.sampleWithEffects.sampleIndex,
    clip.sampleWithEffects.reverse
  ])

  return {
    audioDataArr,
    clipContainer,
    onResizeLeft,
    onResizeRight,
    left,
    width,
    title,
    onStretch,
    startOffsetPx,
    endOffsetPx
  }
}

export default useClip
